import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePDF } from '../context/PDFContext';
import { useNavigate } from 'react-router-dom';
import { startPaystackCheckout } from '../lib/paystackCheckout';
import { Lock, Zap, FileDown, LogIn, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const DownloadLimitModal = ({ open, onClose }) => {
  const { user, refreshUser } = useAuth();
  const { refreshLock } = usePDF();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);

  if (!open) return null;

  const buy = async (planId) => {
    if (!user) {
      toast.info('Sign in first to purchase');
      navigate('/login');
      return;
    }
    setLoadingPlan(planId);
    try {
      const result = await startPaystackCheckout(planId, {
        onPaid: async () => {
          await refreshLock();
          refreshUser();
        },
      });
      if (result === 'paid') {
        toast.success('Payment confirmed — you can download now!');
        onClose();
      } else if (result === 'failed') {
        toast.error('Payment was not completed. Please try again.');
      }
    } catch (e) {
      toast.error(e.message || 'Something went wrong');
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="paywall-modal">
      <div className="bg-card rounded-xl border border-border max-w-sm w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="text-center">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-amber-600" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Unlock Your Download</h3>
          <p className="text-sm text-muted-foreground mb-6">
            {user
              ? "You've used your free download today. Unlock this document, or go unlimited."
              : "You've used your free download today. Sign in to unlock this document or go unlimited."}
          </p>
          <div className="space-y-2">
            <Button
              className="w-full"
              onClick={() => buy('subscription')}
              disabled={loadingPlan !== null}
              data-testid="paywall-subscribe-btn"
            >
              {loadingPlan === 'subscription' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Get Unlimited Access — R49/mo
            </Button>
            <p className="text-[11px] text-muted-foreground">R1 card check · 3-day trial · cancel anytime</p>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => buy('single')}
              disabled={loadingPlan !== null}
              data-testid="paywall-single-btn"
            >
              {loadingPlan === 'single' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Unlock this document — R8
            </Button>
            {!user && (
              <Button variant="ghost" className="w-full" onClick={() => { onClose(); navigate('/login'); }} data-testid="signin-btn">
                <LogIn className="w-4 h-4 mr-2" /> Sign in with Google
              </Button>
            )}
            <button onClick={onClose} className="text-xs text-muted-foreground hover:underline mt-2 block mx-auto" data-testid="paywall-close">
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadLimitModal;
